import React, { useContext, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Grid, CircularProgress } from '@material-ui/core';
import clsx from 'clsx';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { SmartModuleType, SmartModule } from '@moderepo/mode-internal-apis';
import { GenericMessageBox, capitalizeAll } from '@moderepo/mode-app-components';
import { selectAllHomeTimeSeriesCollectionInfo, fetchAllHomeSmartModules, fetchAllTimeSeriesInfo, selectAllHomeTimeSeries,
    selectHomeSmartModulesByType, userAppDataContext, fetchAllTimeSeriesCollectionInfo } from '@moderepo/mode-data-state-user-app';
import { useRitzTranslation } from '../../utils/customHooks';
import { CollectionsListingContainer } from '../time-series/CollectionsListingContainer';
import { TimeSeriesListingContainer } from '../time-series/TimeSeriesListingContainer';



export const HomeTSDBPage: React.FC = () => {
    // Get the params from the route or url
    const params = useParams();
    const projectId = Number(params.projectId);
    const userId = params.userId ? Number(params.userId) : undefined;       // userId is OPTIONAL depending on which route the user is on
    const homeId = Number(params.homeId);

    const { state:dataState } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const project = selectProjectById(dataState, projectId);
    const { trans } = useRitzTranslation(project);
    const tsdbSmartModules = selectHomeSmartModulesByType(appDataState, homeId, SmartModuleType.TSDB);

    /**
     * This useEffect is used for fetching all the smart modules this home has
     */
    useEffect(() => {
        if (!tsdbSmartModules) {
            appDataDispatch(fetchAllHomeSmartModules(homeId)).catch(() => {
                // ignore error
            });
        }
    }, [appDataDispatch, homeId, tsdbSmartModules]);

    /**
     * This useEffect is used for fetching time series info
     */
    useEffect(() => {
        if (tsdbSmartModules && tsdbSmartModules.length > 0) {
            // fetch all time series info for each TSDB smart modules
            tsdbSmartModules.forEach((module: SmartModule) => {
                appDataDispatch(fetchAllTimeSeriesInfo(homeId, module.id), true);
            });
        }
    }, [appDataDispatch, homeId, tsdbSmartModules]);

    /**
     * This use effect is used for fetching all time series collections info
     */
    useEffect(() => {
        if (tsdbSmartModules && tsdbSmartModules.length > 0) {
            // fetch all collection info for each TSDB smart modules
            tsdbSmartModules.forEach((module: SmartModule) => {
                appDataDispatch(fetchAllTimeSeriesCollectionInfo(homeId, module.id), true);
            });
        }
    }, [appDataDispatch, homeId, tsdbSmartModules]);

    /**
     * Get the home time series info from the data state
     */
    const allHomeTimeSeries = useMemo(() => {
        if (tsdbSmartModules) {
            const result = tsdbSmartModules.map((module: SmartModule) => {
                return selectAllHomeTimeSeries(appDataState, homeId, module.id) || [];
            }).flat();
            return result;
        }
        return undefined;
    }, [appDataState, homeId, tsdbSmartModules]);

    /**
     * Get the home collection info from the data state
     */
    const allHomeCollections = useMemo(() => {
        if (tsdbSmartModules) {
            const result = tsdbSmartModules.map((module: SmartModule) => {
                return selectAllHomeTimeSeriesCollectionInfo(appDataState, homeId, module.id) || [];
            }).flat();
            return result;
        }
        return undefined;
    }, [appDataState, homeId, tsdbSmartModules]);

    /**
     * Check to see if there are Time Series data or Collections data
     */
    const hasTimeSeries = allHomeTimeSeries && allHomeTimeSeries.length > 0;
    const hasCollections = allHomeCollections && allHomeCollections.length > 0;
    const hasNoData = !hasTimeSeries && !hasCollections;

    return (
        <div className={clsx('page-main-content')}>
            <div className="title-bar">
                <div className="title-bar-item title-text">
                    <div className="page-title">
                        {trans('page.title.home_data')}
                    </div>
                    <div className="page-subtitle">
                        {trans('page.subtitle.home_data')}
                    </div>
                </div>
            </div>


            <div>
                {hasNoData && (
                    <GenericMessageBox
                        title={capitalizeAll(trans('generic.no_data.title', { term: trans('label.data', { count: 2 }) }))}
                    />
                )}
                <Grid container spacing={3}>
                    {tsdbSmartModules && hasTimeSeries && (
                        <Grid item xs={12}>
                            <TimeSeriesListingContainer projectId={projectId} userId={userId} homeId={homeId} hiddenFields={{ homeId: true }} />
                        </Grid>
                    )}
                    {tsdbSmartModules && hasCollections && (
                        <Grid item xs={12}>
                            <CollectionsListingContainer projectId={projectId} userId={userId} homeId={homeId} hiddenFields={{ homeId: true }} />
                        </Grid>
                    )}
                </Grid>
            </div>

            {!project && (
                <CircularProgress />
            )}
        </div>
    );
};
